import { InboxOutlined, SaveOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Result, Steps, Table, Upload } from 'antd';
import { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import Papa from 'papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { syncAppointments } from 'src/modules/ambient_solo/store/actions/appointments';
import {
  getCSVColumnMappings,
  uploadCSVAction,
} from 'src/store/actions/appointment.action';
import { showToastSuccess } from 'src/utilities/toast';
import XLSX from 'xlsx';
import '../styles/AppointmentsCSVUploader.scss';
import moment from 'moment';
import { getDacSettingsEnabled } from 'src/store/actions/settings.action';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

const { Step } = Steps;
const { Dragger } = Upload;

const schema = {
  columns: [
    { name: 'Created Date Time', key: 'created_date_time', data_type: 'date' },
    { name: 'Start Date Time', key: 'start_date_time', data_type: 'date' },
    { name: 'Start Date', key: 'start_date', data_type: 'date' },
    { name: 'Start Time', key: 'start_time', data_type: 'time' },
    { name: 'Provider', key: 'provider', data_type: 'string' },
    { name: 'Patient', key: 'patient', data_type: 'string' },
    {
      name: 'Patient First Name',
      key: 'patient_first_name',
      data_type: 'string',
    },
    {
      name: 'Patient Last Name',
      key: 'patient_last_name',
      data_type: 'string',
    },
    {
      name: 'Patient EHR/MRN ID',
      key: 'patient_ehr_id',
      data_type: 'string',
    },
    { name: 'Phone Number', key: 'phone_number', data_type: 'string' },
    { name: 'Email', key: 'email', data_type: 'string' },
    { name: 'Appointment Type', key: 'appointment_type', data_type: 'string' },
    {
      name: 'Appointment Status',
      key: 'appointment_status',
      data_type: 'string',
    },
    { name: 'Birth Date', key: 'birth_date', data_type: 'date' },
    { name: 'Sex', key: 'sex', data_type: 'string' },
    {
      name: 'Primary Address 1',
      key: 'primary_address_1',
      data_type: 'string',
    },
    {
      name: 'Primary Address 2',
      key: 'primary_address_2',
      data_type: 'string',
    },
    { name: 'City', key: 'city', data_type: 'string' },
    { name: 'State', key: 'state', data_type: 'string' },
    { name: 'Zip', key: 'zip', data_type: 'string' },
    {
      name: 'Appointment Notes',
      key: 'appt_notes',
      data_type: 'string',
    },
    { name: 'Misc. Column', key: 'misc_column', data_type: 'string' },
  ],
};

export const AppointmentsCSVUploader = ({ visible, setVisible }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [csvData, setCSVData] = useState([]);
  const [file, setFile] = useState(null);
  const [_uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, _setUploadResult] = useState(null);
  const [csvUploadedToS3, setCsvUploadedToS3] = useState(true);
  const [missingColumns, setMissingColumns] = useState([]);
  const [missingColumnsError, setMissingColumnsError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { csvColumnMappings, csvDateTimeFormats } = useSelector(
    (state) => state.appointmentState
  );
  const { user } = useAuth();

  // Fetch CSV column mappings when the modal is opened
  useEffect(() => {
    if (visible) {
      dispatch(getCSVColumnMappings(currentPractitioner?.org_uuid));
    }
  }, [visible]);

  const handleClose = () => {
    setCurrentStep(0);
    setCSVData([]);
    setUploadProgress(0);
    setVisible(false);
    setMissingColumns([]);
    setMissingColumnsError(false);
    setFileError(false);
    setFileErrorMessage('');
  };

  const getRecords = (jsonData) => {
    let appointments = [];
    let rowNumber = 2; // Start row number from 2 to avoid header row
    for (const record of jsonData) {
      appointments.push({
        row_number: rowNumber,
        appt_notes: record.appt_notes?.trim() || '',
        misc_column: record.misc_column?.trim() || '',
        appointment: {
          appointment_status: record.appointment_status || '',
          created_time: record.created_date_time || '',
          start_time: record.start_date_time
            ? record.start_date_time
            : record.start_date
              ? record.start_date + ' ' + record.start_time || ''
              : '',
          practitioner_name: record.provider?.trim() || '',
          condition_name: record.appointment_type || '',
        },
        patient: {
          birth_date: record.birth_date || '',
          name: record.patient
            ? record.patient?.trim()
            : record.patient_first_name?.trim() +
                ' ' +
                record.patient_last_name?.trim() || '',
          ehr_id: record.patient_ehr_id || '',
          gender: record.sex || '',
          address: {
            line_1: record.primary_address_1 || '',
            line_2: record.primary_address_2 || '',
            city: record.city || '',
            state: record.state || '',
            zip: String(record.zip || ''),
          },
          phone: record.phone_number ? String(record.phone_number).trim() : '',
          email: record.email?.trim() || '',
        },
      });
      rowNumber++;
    }
    return appointments;
  };

  const handleCSVUpload = async (file) => {
    try {
      setFileError(false);
      setFileErrorMessage('');
      let schemaCopy = JSON.parse(JSON.stringify(schema));

      if (Object.keys(csvColumnMappings).length != 0) {
        for (const column of schemaCopy.columns) {
          if (
            csvColumnMappings[column.name] &&
            csvColumnMappings[column.name] !== ''
          ) {
            column.name = csvColumnMappings[column.name];
          }
        }
      }

      const dacEnabled = await dispatch(getDacSettingsEnabled());
      const fileType = file.type === 'text/csv' ? 'CSV' : 'Excel';

      analytics.track(
        `Selected A ${fileType} File For Bulk Upload Appointments`,
        { email: user?.email }
      );
      setFile(file); // Store the file for later use

      let jsonData = [];
      if (file.type === 'text/csv') {
        const csvContent = await file.text(); // Read the file content as text

        // Parse CSV to JSON
        const parsedData = Papa.parse(csvContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          transformHeader: (header) => header.trim(),
          transform: (value) => {
            if (typeof value === 'string') {
              return value.replace(/\r\n/g, '\n');
            }
            return value;
          },
        });

        // Map parsed data to schema dynamically
        jsonData = parsedData.data.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            if (dacEnabled) {
              if (
                column.key === 'start_date_time' ||
                column.key === 'provider'
              ) {
                rowJson[column.key] = '';
              } else {
                rowJson[column.key] = row[column.name];
              }
            } else {
              rowJson[column.key] = row[column.name];
            }
          });
          return rowJson;
        });
      } else if (
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel'
      ) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const is1904 = workbook.Workbook?.WBProps?.date1904;

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const parsedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: true,
        });

        // Helper function to convert format strings
        const convertDateFormat = (format) => {
          if (!format) return 'MM/DD/YYYY';
          return format
            .replace(/%m/g, 'MM')
            .replace(/%d/g, 'DD')
            .replace(/%Y/g, 'YYYY')
            .replace(/%y/g, 'YY')
            .replace(/%H/g, 'HH') // 24-hour format (00-23)
            .replace(/%I/g, 'hh') // 12-hour format with leading zero (01-12)
            .replace(/%M/g, 'mm')
            .replace(/%S/g, 'ss')
            .replace(/%p/g, 'A') // AM/PM
            .replace(/%-H/g, 'H') // 24-hour format without leading zero (0-23)
            .replace(/%-I/g, 'h') // 12-hour format without leading zero (1-12)
            .replace(/%-m/g, 'M') // month without leading zero (1-12)
            .replace(/%-d/g, 'D'); // day without leading zero (1-31)
        };

        function excelDateTimeToFullDateTime(serial) {
          if (typeof serial !== 'number') return serial;

          // Split into date and time parts
          const datePart = Math.floor(serial);
          const timePart = serial - datePart;

          // Convert date part
          const epochYear = is1904 ? 1904 : 1900;
          const epochAdjustment = is1904 ? 0 : 2;
          const excelEpoch = new Date(epochYear, 0, 1);
          const days = datePart - epochAdjustment;
          const date = new Date(
            excelEpoch.getTime() + days * 24 * 60 * 60 * 1000
          );

          // Only add time part if timePart is not 0
          if (timePart === 0) {
            return moment(date).format(
              convertDateFormat(csvDateTimeFormats.date_format)
            );
          }

          // Convert time part
          const totalMilliseconds = Math.round(timePart * 24 * 60 * 60 * 1000);

          const timeDate = new Date(totalMilliseconds);

          const hours = timeDate.getUTCHours();
          const minutes = timeDate.getUTCMinutes();
          const seconds = timeDate.getUTCSeconds();

          const timeMoment = moment()
            .hour(hours)
            .minute(minutes)
            .second(seconds);

          return (
            moment(date).format(
              convertDateFormat(csvDateTimeFormats.date_format)
            ) +
            ' ' +
            timeMoment.format(convertDateFormat(csvDateTimeFormats.time_format))
          );
        }

        function excelDateToFullDate(serial) {
          if (typeof serial !== 'number') return serial;
          const epochYear = is1904 ? 1904 : 1900;
          const epochAdjustment = is1904 ? 0 : 2;

          const excelEpoch = new Date(epochYear, 0, 1);
          const days = serial - epochAdjustment;
          const date = new Date(
            excelEpoch.getTime() + days * 24 * 60 * 60 * 1000
          );

          return moment(date).format(
            convertDateFormat(csvDateTimeFormats.date_format)
          );
        }

        function excelTimeToFullTime(serial) {
          if (typeof serial !== 'number') return serial;

          const totalMilliseconds = Math.round(serial * 24 * 60 * 60 * 1000);

          const timeDate = new Date(totalMilliseconds);

          const hours = timeDate.getUTCHours();
          const minutes = timeDate.getUTCMinutes();
          const seconds = timeDate.getUTCSeconds();

          const timeMoment = moment()
            .hour(hours)
            .minute(minutes)
            .second(seconds);

          return timeMoment.format(
            convertDateFormat(csvDateTimeFormats.time_format)
          );
        }

        const headers = parsedData[0].map((header) => header.trim());
        if (dacEnabled) {
          headers.push('Start Date Time');
          headers.push('Provider');
        }

        const dateTimeColumns = ['start_date_time', 'created_date_time'];
        const timeColumns = ['start_time'];
        const dateColumns = ['start_date', 'birth_date'];

        const convertedData = parsedData
          .slice(1)
          .filter((row) => row.length > 0);

        jsonData = convertedData.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            const headerIndex = headers.findIndex(
              (header) => header === column.name
            );
            if (headerIndex !== -1) {
              let value = row[headerIndex] ?? null;

              // Convert based on column type or name patterns
              if (value !== null && typeof value === 'number') {
                if (dateColumns.includes(column.key) && value > 1000) {
                  value = excelDateToFullDate(value);
                } else if (timeColumns.includes(column.key) && value < 1) {
                  value = excelTimeToFullTime(value);
                } else if (
                  dateTimeColumns.includes(column.key) &&
                  value > 1000
                ) {
                  value = excelDateTimeToFullDateTime(value);
                }
              }

              rowJson[column.key] = value;
            }
          });
          return rowJson;
        });
      }

      const requiredColumns = [
        'phone_number',
        'email',
        'appointment_type',
        'appointment_status',
        'birth_date',
      ];

      const firstRow = jsonData[0];

      // Check for date/time and patient columns
      const patientColumns =
        firstRow['patient'] !== undefined
          ? ['patient']
          : ['patient_first_name', 'patient_last_name'];

      const allRequiredColumns = [...requiredColumns, ...patientColumns];

      const missingColumns = allRequiredColumns.filter(
        (key) => firstRow[key] === undefined
      );

      if (missingColumns.length > 0) {
        const missingColumnNames = missingColumns.reduce((acc, key) => {
          const specialCases = {
            patient: 'Patient or Patient First Name & Patient Last Name',
            patient_first_name:
              'Patient or Patient First Name & Patient Last Name',
            patient_last_name:
              'Patient or Patient First Name & Patient Last Name',
          };

          const columnName =
            specialCases[key] ||
            schemaCopy.columns.find((column) => column.key === key)?.name;
          return columnName && !acc.includes(columnName)
            ? [...acc, columnName]
            : acc;
        }, []);

        setMissingColumns(missingColumnNames);
        setMissingColumnsError(true);
      }

      setCSVData(jsonData);

      handleNext();
    } catch (error) {
      setFileError(true);
      setFileErrorMessage(error.message);
      return false;
    }
  };

  const generateColumns = () => {
    if (!csvData || csvData.length === 0) {
      return [];
    }
    const firstRow = csvData[0];
    const columnsToHideIfNotPresent = [
      'patient',
      'patient_first_name',
      'patient_last_name',
      'start_date_time',
      'start_date',
      'start_time',
      'appt_notes',
      'misc_column',
    ];

    return schema.columns
      .map((column) => {
        if (
          columnsToHideIfNotPresent.includes(column.key) &&
          firstRow[column.key] === undefined
        ) {
          return null;
        }
        return {
          title: column.name === 'Sex' ? 'Gender' : column.name,
          dataIndex: column.key,
          key: column.key,
        };
      })
      .filter(Boolean); // Remove null entries
  };
  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const resetProgress = () => {
    setUploadProgress(0);
    setCurrentStep(1);
    analytics.track(
      'Failed To Calculate Time Needed To Upload CSV File For Bulk Upload Appointments',
      { email: user?.email }
    );
  };

  const uploadToS3 = async (file, key) => {
    showToastSuccess('Uploading file...');

    const base64File = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
      reader.onerror = reject;
      reader.readAsDataURL(file); // [<mediatype>][;base64],<data>
    });

    const params = {
      s3_key: key,
      body: base64File,
      content_type: file.type,
    };

    await dispatch(uploadCSVAction(params));
    setCsvUploadedToS3(true);
  };

  const handleSubmit = async () => {
    analytics.track('Submitted CSV File For Bulk Upload Appointments', {
      email: user?.email,
    });
    setCsvUploadedToS3(false);

    const appointments = getRecords(csvData);

    const date = new Date();
    const s3_key =
      `${date.getUTCFullYear()}/${('0' + (date.getUTCMonth() + 1)).slice(
        -2
      )}/${('0' + date.getUTCDate()).slice(-2)}/` +
      `${date.getTime()}` +
      `.${file.name.split('.').pop()}`;

    const syncAppointmentsPayload = {
      s3_key: s3_key,
      appointments: appointments,
    };

    // Make the actual upload call.
    dispatch(syncAppointments(syncAppointmentsPayload)).then(async (result) => {
      // clearInterval(timer);

      if (result) {
        analytics.track(
          'Successfully Uploaded CSV File For Bulk Upload Appointments',
          { email: user?.email }
        );
        await uploadToS3(file, s3_key);
        setCurrentStep(2);
      } else {
        resetProgress();
        setCsvUploadedToS3(true);
      }
    });
  };

  const steps = [
    {
      title: 'Upload file',
      content: (
        <>
          {fileError ? (
            <div className="flex justify-center items-center h-64 mt-12">
              <Alert
                message="Error Processing File"
                description={
                  <>
                    <p>{fileErrorMessage}</p>
                    <div className="mt-2 font-medium">
                      <p>
                        Please contact{' '}
                        <a href="mailto:help@insighthealth.ai">
                          help@insighthealth.ai
                        </a>{' '}
                        for support.
                      </p>
                    </div>
                  </>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <div className="flex-container items-center justify-center">
              <div className="w-3/4 items-center justify-center pt-12">
                <Dragger
                  accept=".csv,.xlsx,.xls"
                  beforeUpload={handleCSVUpload}
                  height={250}
                >
                  <p className="ant-upload-drag-icon text-xs">
                    <InboxOutlined />
                  </p>
                  <p className="text-xs ant-upload-text">
                    Click or drag CSV, XLSX, or XLS file to this area to upload
                  </p>
                </Dragger>
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Preview changes',
      content: (
        <>
          {missingColumnsError ? (
            <div className="flex justify-center items-center h-64 mt-12">
              <Alert
                message="Uploaded file is missing the following required column(s)"
                description={
                  <>
                    <div>
                      <ul
                        style={{ listStyleType: 'disc', paddingLeft: '20px' }}
                      >
                        {missingColumns.map((column, index) => (
                          <li key={index}>{column}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 font-medium">
                      <p>
                        Please contact{' '}
                        <a href="mailto:help@insighthealth.ai">
                          help@insighthealth.ai
                        </a>{' '}
                        for support.
                      </p>
                    </div>
                  </>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <>
              <div className="text-xs mb-3 ml-1">
                Total Rows: {csvData.length}
              </div>
              <Table
                dataSource={csvData}
                columns={generateColumns()}
                pagination={false}
              />
            </>
          )}
        </>
      ),
    },
    {
      title: 'Result',
      content: (
        <>
          <Result
            status="success"
            title="Upload Complete"
            subTitle="An email summary will be sent to you shortly."
          />
          {uploadResult?.errors && (
            <div className="flex flex-col items-center">
              <Table
                dataSource={uploadResult.errors.map((error, index) => ({
                  key: index,
                  reason: error,
                }))}
                pagination={false}
              >
                <Table.Column
                  title="Reason for skipping"
                  dataIndex="reason"
                  key="reason"
                  className="text-xs"
                />
              </Table>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="85vw"
      className="csv-uploader text-xs"
    >
      <Steps current={currentStep}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} />
        ))}
      </Steps>
      <>
        <div className="steps-action flex justify-right mb-2">
          {currentStep === 1 &&
            !missingColumnsError &&
            (csvUploadedToS3 ? (
              <Button
                type="primary"
                onClick={handleSubmit}
                icon={<SaveOutlined />}
              >
                Submit
              </Button>
            ) : (
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            ))}
        </div>
        <div className="steps-content">{steps[currentStep].content}</div>
      </>
    </Modal>
  );
};

export default AppointmentsCSVUploader;
